import { ReactComponent as ArrowIcon } from '@assets/icons/arrow_grey_up_right_icon.svg';
import StatusChip from '@components/widgets/StatusChip';
import {
  INSPECTION_SECURITY_CHECK_VARIANT,
  INSPECTION_SECURITY_CHECK_LABEL_STATUS
} from '@constants';
import { DocumentFormType, InspectionSecurityStatusEnum } from '@types';
import clsx from 'clsx';
const stringFormat = (template: string, ...args: any[]) => {
  return template.replace(/{(\d+)}/g, function (match, number) {
    return typeof args[number] != 'undefined' ? args[number] : match;
  });
};

const SECURITY_CHECK_TYPES = {
  AUTO_GRAB: 'AUTOGRAB_DATA',
  NEVDIS_REGISTRATION: 'NEVDIS_REGISTRATION',
  NEVDIS_STOLEN: 'NEVDIS_STOLEN',
  NEVDIS_WRITTEN_OFF: 'NEVDIS_WRITTEN_OFF',
  PPSR_ENCUMBRANCE: 'PPSR_ENCUMBRANCE',
  MARINE_PPSR_ENCUMBRANCE: 'MARINE_PPSR_ENCUMBRANCE',
  RAV_COMPLIANCE: 'RAV_COMPLIANCE'
} as const;

const getReportCountLabel = (count: number) =>
  count > 1 ? `${count} Reports Found ` : '1 Report Found';

const getDefaultLabel = (status: string) =>
  INSPECTION_SECURITY_CHECK_LABEL_STATUS[
    status as keyof typeof INSPECTION_SECURITY_CHECK_LABEL_STATUS
  ];

const getDefaultVariant = (status: string) =>
  INSPECTION_SECURITY_CHECK_VARIANT[status as InspectionSecurityStatusEnum];

const PPSR_CHECK_TYPES = [
  SECURITY_CHECK_TYPES.PPSR_ENCUMBRANCE,
  SECURITY_CHECK_TYPES.MARINE_PPSR_ENCUMBRANCE
];

const SECURITY_CHECK_KEYS = [
  'rav',
  'registration',
  'ppsr',
  'stolen',
  'writtenOff',
  'autoGrabData'
];

const NON_CLICKABLE_STATUSES = [
  InspectionSecurityStatusEnum.notPerformed,
  InspectionSecurityStatusEnum.pass,
  InspectionSecurityStatusEnum.noRegister,
  InspectionSecurityStatusEnum.noReport,
  InspectionSecurityStatusEnum.noRecord,
  InspectionSecurityStatusEnum.notFound,
  InspectionSecurityStatusEnum.match,
  InspectionSecurityStatusEnum.error
];

const VISIBLE_NOT_REQUESTED_KEYS = [
  'ppsr',
  'rav',
  'registration',
  'stolen',
  'writtenOff',
  'autoGrabData'
];

const DETAIL_LINK_EXCLUDED_KEYS = [
  'ppsr',
  'rav',
  'registration',
  'stolen',
  'writtenOff',
  'autoGrabData'
];

const CONTENT_HIDDEN_STATUSES = [
  InspectionSecurityStatusEnum.notPerformed,
  InspectionSecurityStatusEnum.error
];

const DETAIL_LINK_HIDDEN_STATUSES = [
  InspectionSecurityStatusEnum.noReport,
  InspectionSecurityStatusEnum.noRecord
];

const Variants = {
  success_light:
    'bg-success-50 border-success-500 active:bg-success-800 hover:bg-success-800',
  info_light:
    'border-grey-300 bg-grey-25 hover:bg-grey-100 focus:bg-grey-100 active:bg-grey-200',
  warning_light:
    'border-warning-500 bg-warning-50 active:bg-warning-700 hover:bg-warning-700',
  error_light:
    'border-error-400 bg-error-50 active:!bg-error-100 hover:bg-error-100',
  none_light:
    'border-grey-300 bg-grey-50 hover:bg-grey-100 focus:bg-grey-100 active:bg-grey-200'
};

export type SecurityCheckVariant =
  | 'success_light'
  | 'none_light'
  | 'info_light'
  | 'warning_light'
  | 'info_light';

const isRegistrationCheck = (type: string) =>
  type === SECURITY_CHECK_TYPES.NEVDIS_REGISTRATION;

const hasRegistrationDetails = (type: string, documentObj: any) =>
  isRegistrationCheck(type) && documentObj.expiry && documentObj.expiry !== '';

const isRowClickable = (status: string, type: string, documentObj: any) => {
  if (type === SECURITY_CHECK_TYPES.AUTO_GRAB) {
    return status === InspectionSecurityStatusEnum.reportFound;
  }

  return (
    (status &&
      !NON_CLICKABLE_STATUSES.includes(
        status as InspectionSecurityStatusEnum
      )) ||
    (hasRegistrationDetails(type, documentObj) &&
      [
        InspectionSecurityStatusEnum.noRegister,
        InspectionSecurityStatusEnum.discrepancyFound
      ].includes(status as InspectionSecurityStatusEnum))
  );
};

const shouldHideRow = (key: string, status: string) =>
  status === InspectionSecurityStatusEnum.notRequested &&
  !VISIBLE_NOT_REQUESTED_KEYS.includes(key);

const shouldShowRowContent = (status: string) =>
  !CONTENT_HIDDEN_STATUSES.includes(status as InspectionSecurityStatusEnum);

const shouldShowDetailsLink = (
  key: string,
  status: string,
  type: string,
  documentObj: any
) =>
  !DETAIL_LINK_EXCLUDED_KEYS.includes(key) &&
  (hasRegistrationDetails(type, documentObj) ||
    (!isRegistrationCheck(type) &&
      !DETAIL_LINK_HIDDEN_STATUSES.includes(
        status as InspectionSecurityStatusEnum
      )));

const SecurityCheck = ({
  docType,
  data,
  setType,
  setStatus,
  setIsCollapsed,
  setVariant,
  setStatusChipLabel,
  showAutoGrab = true
}: {
  docType: object;
  data: any;
  setType: (type: DocumentFormType) => void;
  setStatus: (status: InspectionSecurityStatusEnum) => void;
  setIsCollapsed: (isCollapsed: boolean) => void;
  setVariant: (variant: string) => void;
  setStatusChipLabel: (statusChipLabel: string) => void;
  showAutoGrab?: boolean;
}) => {
  const handleClick = (
    status: any,
    key: string,
    type: string,
    documentObj: any
  ) => {
    if (!isRowClickable(status, type, documentObj)) {
      return;
    }
    setType(key as DocumentFormType);
    setStatus(status);
    setVariant(getVariant(status, type, documentObj));
    setStatusChipLabel(getLabel(status, type, documentObj));
    setIsCollapsed(false);
  };

  const getVariant = (status: string, type: string, documentObj: any) => {
    if (PPSR_CHECK_TYPES.includes(type as any)) {
      if (status === InspectionSecurityStatusEnum.reportFound) {
        return 'warning_light';
      }
      if (status === InspectionSecurityStatusEnum.noRecord) {
        return 'success_light';
      }
    }

    switch (type) {
      case SECURITY_CHECK_TYPES.RAV_COMPLIANCE:
        if (status === InspectionSecurityStatusEnum.reportFound) {
          return 'success_light';
        }
        if (status === InspectionSecurityStatusEnum.noReport) {
          return 'info_light';
        }
        break;

      case SECURITY_CHECK_TYPES.AUTO_GRAB:
        if (status === InspectionSecurityStatusEnum.reportFound) {
          return 'success_light';
        }
        break;

      case SECURITY_CHECK_TYPES.NEVDIS_REGISTRATION:
        if (
          status === InspectionSecurityStatusEnum.reportFound &&
          documentObj.regoStatus ===
            InspectionSecurityStatusEnum.registered.toLocaleUpperCase()
        ) {
          return 'success_light';
        }
        if (status === InspectionSecurityStatusEnum.noReport) {
          return 'info_light';
        }
        break;
    }

    return getDefaultVariant(status);
  };

  const getLabel = (status: string, type: string, documentObj: any) => {
    if (status === InspectionSecurityStatusEnum.notRequested) {
      return 'Disabled';
    }

    switch (type) {
      case SECURITY_CHECK_TYPES.AUTO_GRAB:
        if (status === InspectionSecurityStatusEnum.reportFound) {
          return 'Received';
        }
        if (status === InspectionSecurityStatusEnum.error) {
          return 'Error';
        }
        break;

      case SECURITY_CHECK_TYPES.NEVDIS_REGISTRATION:
        if (status === InspectionSecurityStatusEnum.expired) return 'Expired';
        if (
          status === InspectionSecurityStatusEnum.reportFound &&
          documentObj.regoStatus ===
            InspectionSecurityStatusEnum.registered.toLocaleUpperCase()
        ) {
          return 'Registered';
        }
        if (status === InspectionSecurityStatusEnum.noReport) {
          return 'Not Found';
        }
        break;

      case SECURITY_CHECK_TYPES.NEVDIS_STOLEN:
        if (status === InspectionSecurityStatusEnum.reportFound) {
          return getReportCountLabel(documentObj.stolenDetails.length);
        }
        break;

      case SECURITY_CHECK_TYPES.NEVDIS_WRITTEN_OFF:
        if (status === InspectionSecurityStatusEnum.reportFound) {
          return getReportCountLabel(documentObj.writtenOffDetails.length);
        }
        break;

      case SECURITY_CHECK_TYPES.PPSR_ENCUMBRANCE:
      case SECURITY_CHECK_TYPES.MARINE_PPSR_ENCUMBRANCE:
        if (status === InspectionSecurityStatusEnum.reportFound) {
          return stringFormat(
            INSPECTION_SECURITY_CHECK_LABEL_STATUS[
              InspectionSecurityStatusEnum.registrations
            ],
            documentObj.ppsrData.length
          );
        }
        if (status === InspectionSecurityStatusEnum.noRecord) {
          return 'No Registrations Found';
        }
        break;

      case SECURITY_CHECK_TYPES.RAV_COMPLIANCE:
        if (status === InspectionSecurityStatusEnum.noReport) {
          return 'No Found';
        }
        if (status === InspectionSecurityStatusEnum.reportFound) {
          return 'Received';
        }
        break;
    }

    return getDefaultLabel(status);
  };
  return (
    <>
      <div className="text-md">
        <h1 className="text-black font-semibold">Vehicle Check</h1>
      </div>
      <div className="border-grey-200  w-full flex flex-col justify-center h-min">
        {SECURITY_CHECK_KEYS.map((key, index) => {
          if (key === 'autoGrabData' && !showAutoGrab) return null;

          const documentObj = data?.[key];
          if (!documentObj) return null;

          const type = documentObj.type;
          const status =
            documentObj.status || InspectionSecurityStatusEnum.notPerformed;
          const label = docType[key as keyof typeof docType];
          const variant = getVariant(status, type, documentObj);
          const variantClass = Variants[variant as keyof typeof Variants];
          const clickable = isRowClickable(status, type, documentObj);

          return (
            <div
              className={clsx(
                'flex border rounded-xl px-3.5 py-3 items-center justify-between mt-2 whitespace-nowrap flex-wrap items-start',
                clickable && 'cursor-pointer',
                index === 0 && 'first:mt-0 cursor-default',
                variantClass,
                shouldHideRow(key, status) && 'hidden'
              )}
              key={key}
              onClick={() => handleClick(status, key, type, documentObj)}>
              <div className="flex flex-row items-center whitespace-nowrap gap-3">
                <span className="text-sm font-semibold">{label}</span>
              </div>
              {shouldShowRowContent(status) && (
                <div className="flex flex-wrap flex-row justify-start gap-x-6 whitespace-nowrap">
                  {shouldShowDetailsLink(key, status, type, documentObj) && (
                    <span className="flex flex-row font-semibold justify-center items-center text-sm text-grey-600">
                      <span className="underline">Details</span>
                      <ArrowIcon />
                    </span>
                  )}
                  <StatusChip
                    variant={variant}
                    clsName={clsx('border text-xs font-normal max-w-full')}
                    label={getLabel(status, type, documentObj)}
                  />
                </div>
              )}
            </div>
          );
        })}
        <div></div>
      </div>
    </>
  );
};

export default SecurityCheck;
